import { Grid, Paper, Tabs, Text } from '@mantine/core';
import { IconCheck, IconExclamationCircle, IconX } from '@tabler/icons-react';
import * as React from 'react';
import { DialogSize } from '../../../../../../react-commons/components/data-display/dialog/ResizableDialog';
import { getColorMantine } from '../../../../../../react-commons/components/inputs/text-fields/types';
import { ValidationEntrySeverity } from '../../../../../../react-commons/components/ValidationEntrySeverity';
import { formatToPascalCase } from '../../../../../../react-commons/utils/utilFunctions';
import { ResourceType } from '../../../../resource/enumerations/ResourceType';
import { ValidationType } from '../../../../resource/enumerations/ValidationType';
import { ValidationResource } from '../../../../resource/resources/validation/detailedValidationResource';
import { MutationValidationResource } from '../../../../resource/resources/validation/mutationValidationResource';
import { compareValidationSeverity } from '../../../util/utilFunctions';
import { DependencyValidationResultComponent } from './DependencyValidationResultComponent';
import { SideEffectResultMapComponent } from './SideEffectResultMapComponent';
import { ValidationResourceResultComponent } from './ValidationResourceResultComponent';

export const MutationValidationResultComponent = ({
    resourceValidation,
    resourceType,
    beforeContent,
    dialogSize = DialogSize.LARGE,
}: {
    resourceValidation?: MutationValidationResource | null;
    resourceType: ResourceType;
    beforeContent?: React.ReactElement;
    dialogSize?: DialogSize;
}) => {
    const getMaxResourceValidationSeverity = (
        validationResource: ValidationResource | undefined | null
    ): ValidationEntrySeverity | undefined => {
        if (validationResource) {
            if (
                validationResource.FATAL &&
                (validationResource.FATAL.propertyValidation.length > 0 ||
                    validationResource.FATAL.generalValidation.length > 0 ||
                    validationResource.FATAL.childValidation.length > 0)
            ) {
                return ValidationEntrySeverity.FATAL;
            } else if (
                validationResource.CRITICAL &&
                (validationResource.CRITICAL.propertyValidation.length > 0 ||
                    validationResource.CRITICAL.generalValidation.length > 0 ||
                    validationResource.CRITICAL.childValidation.length > 0)
            ) {
                return ValidationEntrySeverity.CRITICAL;
            } else if (
                validationResource.WARNING &&
                (validationResource.WARNING.propertyValidation.length > 0 ||
                    validationResource.WARNING.generalValidation.length > 0 ||
                    validationResource.WARNING.childValidation.length > 0)
            ) {
                return ValidationEntrySeverity.WARNING;
            } else if (
                validationResource.NOTE &&
                (validationResource.NOTE.propertyValidation.length > 0 ||
                    validationResource.NOTE.generalValidation.length > 0 ||
                    validationResource.NOTE.childValidation.length > 0)
            ) {
                return ValidationEntrySeverity.NOTE;
            }
        }
        return undefined;
    };

    const getMaxDependencySeverity = () => {
        const dependency = resourceValidation?.dependencyValidationResult;
        if (dependency) {
            let currentMax: ValidationEntrySeverity | undefined;
            for (const groupValue of Object.keys(dependency)) {
                for (const typeGroup of Object.keys(dependency[groupValue])) {
                    for (const value of Object.keys(dependency[groupValue][typeGroup])) {
                        const dep = dependency[groupValue][typeGroup][value];
                        const max = getMaxResourceValidationSeverity(dep.validationResultMap);

                        if (max === ValidationEntrySeverity.FATAL) {
                            return max;
                        }
                        if (max !== undefined && (!currentMax || compareValidationSeverity(currentMax, max) > 0)) {
                            currentMax = max;
                        }
                    }
                }
            }
            return currentMax;
        }
        return undefined;
    };

    const getDefaultState = () => {
        if (getMaxResourceValidationSeverity(resourceValidation?.validationResult)) {
            return 'Resource Validation';
        } else if (getMaxDependencySeverity()) {
            return 'Dependency';
        }
        return 'Side Effect';
    };

    const [selectedValidationType, setSelectedValidationType] = React.useState<string | null>(getDefaultState());

    React.useEffect(() => {
        if (resourceValidation) {
            if (getMaxResourceValidationSeverity(resourceValidation?.validationResult)) {
                setSelectedValidationType('Resource Validation');
            } else if (getMaxDependencySeverity()) {
                setSelectedValidationType('Dependency');
            } else if (resourceValidation?.sideEffectResult?.maxSeverity) {
                setSelectedValidationType('Side Effect');
            }
        }
    }, [resourceValidation]);

    const addChanged = !!resourceValidation?.changed && resourceValidation.changed !== resourceValidation.successful;

    const textSize = (text: string) => {
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        if (context === null) {
            return 0;
        }

        context.font =
            '16px -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Helvetica, Arial, sans-serif, Apple Color Emoji, Segoe UI Emoji';

        return context.measureText(text).width;
    };

    const lineLimit =
        dialogSize === DialogSize.LARGE ? 1420
        : dialogSize === DialogSize.SMALL_MEDIUM ? 420
        : 100;

    const transformText = (properties: string[]): string[] => {
        const unified: string[] = [];
        let currentLine = properties.shift() ?? '';
        for (const property of properties) {
            if (textSize(`${currentLine}, ${property},`) > lineLimit) {
                unified.push(`${currentLine},`);
                currentLine = property;
            } else {
                currentLine += `, ${property}`;
            }
        }
        unified.push(currentLine);
        return unified;
    };

    return (
        <div style={{ minWidth: 1200 }}>
            <Grid>
                {beforeContent && <Grid.Col span={12}>{beforeContent}</Grid.Col>}
                <Grid.Col span={12}>
                    <Paper style={{ padding: 4, display: 'inline-flex', flexDirection: 'column' }} variant={'outlined'}>
                        <div
                            style={{
                                display: 'inline-flex',
                                flexDirection: 'row',
                                alignItems: 'center',
                                padding: 2,
                                width: 'auto',
                            }}
                        >
                            <Text style={{ minWidth: '5.5rem', marginRight: 10 }}>Successful :</Text>
                            {resourceValidation?.successful ?
                                <IconCheck
                                    color={'var(--mantine-primary-color-filled)'}
                                    style={{ marginRight: addChanged ? 20 : 40 }}
                                />
                            :   <IconX
                                    color={getColorMantine(ValidationEntrySeverity.CRITICAL)}
                                    style={{ marginRight: addChanged ? 20 : 40 }}
                                />
                            }
                            {addChanged && (
                                <>
                                    <Text style={{ minWidth: '5.5rem', marginRight: 10 }}>Changed :</Text>
                                    {resourceValidation?.changed ?
                                        <IconCheck
                                            color={'var(--mantine-primary-color-filled)'}
                                            style={{ marginRight: 20 }}
                                        />
                                    :   <IconX
                                            color={getColorMantine(ValidationEntrySeverity.CRITICAL)}
                                            style={{ marginRight: 20 }}
                                        />
                                    }
                                </>
                            )}
                            {resourceValidation?.maxSeverity && (
                                <div style={{ display: 'inline-flex', alignItems: 'center', marginRight: 40 }}>
                                    <Text style={{ minWidth: 'fit-content' }}>Validation Severity :</Text>
                                    <IconExclamationCircle
                                        color={getColorMantine(resourceValidation.maxSeverity)}
                                        style={{ marginRight: 5, marginLeft: 5 }}
                                    />
                                    <Text
                                        style={{
                                            minWidth: 'fit-content',
                                            paddingLeft: 6,
                                            paddingRight: 6,
                                        }}
                                    >
                                        {resourceValidation.maxSeverity}
                                    </Text>
                                </div>
                            )}
                            {resourceValidation?.fatalThreshold && (
                                <div style={{ display: 'inline-flex', alignItems: 'center' }}>
                                    <Text style={{ alignItems: 'center', minWidth: 'fit-content' }}>
                                        Error Threshold :
                                    </Text>
                                    <IconExclamationCircle
                                        color={getColorMantine(resourceValidation.fatalThreshold)}
                                        style={{ marginRight: 5, marginLeft: 5 }}
                                    />
                                    <Text
                                        style={{
                                            minWidth: 'fit-content',
                                            paddingLeft: 6,
                                            paddingRight: 6,
                                        }}
                                    >
                                        {resourceValidation.fatalThreshold}
                                    </Text>
                                </div>
                            )}
                        </div>
                        {resourceValidation?.validationType === ValidationType.UPDATE &&
                            resourceValidation?.changedProperties && (
                                <div
                                    style={{
                                        display: 'inline-flex',
                                        padding: 2,
                                        alignItems: 'center',
                                        maxWidth: '100%',
                                        overflowX: 'auto',
                                    }}
                                >
                                    <Text style={{ minWidth: 'fit-content', marginRight: 10 }}>
                                        Changed Properties :
                                    </Text>
                                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                                        {transformText(
                                            resourceValidation.changedProperties.map(formatToPascalCase)
                                        ).map((it, index) => (
                                            <Text key={index}>{it}</Text>
                                        ))}
                                    </div>
                                </div>
                            )}
                        {resourceValidation?.validationType === ValidationType.UPDATE &&
                            resourceValidation.nonUpdatablePropertiesChanged && (
                                <div
                                    style={{
                                        display: 'inline-flex',
                                        padding: 2,
                                        alignItems: 'center',
                                        maxWidth: '100%',
                                        overflowX: 'auto',
                                    }}
                                >
                                    <Text style={{ minWidth: 'fit-content', marginRight: 10 }}>
                                        Non Updatable Properties Changed :
                                    </Text>
                                    <Text>
                                        {resourceValidation.nonUpdatablePropertiesChanged
                                            .map(formatToPascalCase)
                                            .join(', ')}
                                    </Text>
                                </div>
                            )}
                        {Boolean(Object.keys(resourceValidation?.referencedVersionsNotClosed ?? []).length) && (
                            <div style={{ display: 'inline-flex', padding: 2, width: '100%' }}>
                                <Text style={{ minWidth: 'fit-content', marginRight: 5 }}>Versions Not Closed: </Text>
                                <Text
                                    style={{
                                        minWidth: 'fit-content',
                                        paddingLeft: 6,
                                        paddingRight: 6,
                                    }}
                                >
                                    {Object.keys(resourceValidation?.referencedVersionsNotClosed ?? [])
                                        .map(formatToPascalCase)
                                        .join(', ')}
                                </Text>
                            </div>
                        )}
                    </Paper>
                </Grid.Col>
                {((resourceValidation?.validationResult &&
                    getMaxResourceValidationSeverity(resourceValidation?.validationResult)) ||
                    (resourceValidation?.dependencyValidationResult && getMaxDependencySeverity()) ||
                    (resourceValidation?.sideEffectResult && resourceValidation?.sideEffectResult.maxSeverity)) && (
                    <Grid.Col span={12}>
                        <Paper
                            withBorder
                            style={{
                                minHeight:
                                    Boolean(Object.keys(resourceValidation?.referencedVersionsNotClosed ?? []).length) ?
                                        450
                                    :   500,
                            }}
                        >
                            {!resourceValidation?.dependencyValidationResult && !resourceValidation?.sideEffectResult ?
                                <ValidationResourceResultComponent
                                    maxSeverity={
                                        getMaxResourceValidationSeverity(resourceValidation?.validationResult) ??
                                        ValidationEntrySeverity.NOTE
                                    }
                                    resourceValidation={resourceValidation?.validationResult}
                                    persistableTypeGroup={resourceType}
                                />
                            :   <Tabs value={selectedValidationType} onChange={setSelectedValidationType}>
                                    <Tabs.List>
                                        {resourceValidation?.validationResult &&
                                            getMaxResourceValidationSeverity(resourceValidation?.validationResult) && (
                                                <Tabs.Tab
                                                    value={'Resource Validation'}
                                                    style={{ height: 28, minHeight: 28 }}
                                                    rightSection={
                                                        <IconExclamationCircle
                                                            color={getColorMantine(
                                                                getMaxResourceValidationSeverity(
                                                                    resourceValidation?.validationResult
                                                                )
                                                            )}
                                                        />
                                                    }
                                                >
                                                    Resource Validation
                                                </Tabs.Tab>
                                            )}
                                        {resourceValidation?.dependencyValidationResult &&
                                            getMaxDependencySeverity() && (
                                                <Tabs.Tab
                                                    value={'Dependency'}
                                                    style={{ height: 28, minHeight: 28 }}
                                                    rightSection={
                                                        <IconExclamationCircle
                                                            color={getColorMantine(getMaxDependencySeverity())}
                                                        />
                                                    }
                                                >
                                                    Dependency
                                                </Tabs.Tab>
                                            )}
                                        {resourceValidation?.sideEffectResult && (
                                            <Tabs.Tab
                                                value={'Side Effect'}
                                                style={{ height: 28, minHeight: 28 }}
                                                rightSection={
                                                    <IconExclamationCircle
                                                        color={getColorMantine(
                                                            resourceValidation?.sideEffectResult?.maxSeverity ??
                                                                undefined
                                                        )}
                                                    />
                                                }
                                            >
                                                Side Effect
                                            </Tabs.Tab>
                                        )}
                                    </Tabs.List>
                                    <Tabs.Panel value={'Resource Validation'}>
                                        <Paper withBorder>
                                            {resourceValidation.validationResult && (
                                                <ValidationResourceResultComponent
                                                    maxSeverity={
                                                        getMaxResourceValidationSeverity(
                                                            resourceValidation?.validationResult
                                                        ) ?? ValidationEntrySeverity.NOTE
                                                    }
                                                    resourceValidation={resourceValidation.validationResult}
                                                    persistableTypeGroup={resourceType}
                                                />
                                            )}
                                        </Paper>
                                    </Tabs.Panel>
                                    <Tabs.Panel value={'Dependency'}>
                                        <Paper withBorder>
                                            {resourceValidation.dependencyValidationResult && (
                                                <DependencyValidationResultComponent
                                                    dependencyValidationResult={
                                                        resourceValidation.dependencyValidationResult
                                                    }
                                                    height={550}
                                                />
                                            )}
                                        </Paper>
                                    </Tabs.Panel>
                                    <Tabs.Panel value={'Side Effect'}>
                                        <Paper withBorder>
                                            {resourceValidation.sideEffectResult && (
                                                <SideEffectResultMapComponent
                                                    sideEffectResultMap={resourceValidation.sideEffectResult}
                                                />
                                            )}
                                        </Paper>
                                    </Tabs.Panel>
                                </Tabs>
                            }
                        </Paper>
                    </Grid.Col>
                )}
            </Grid>
        </div>
    );
};
